import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A figure's moment, as a class on its root: no class shows the final drawing, which is
// what a reader gets without script or with reduced motion; 'oh-armed' draws the starting
// state; 'oh-play' runs the CSS that moves it to the final one, once.
export type Stage = 'rest' | 'armed' | 'play';

// A figure's moment waits for main's fade-up (0.7 s) to be mostly done, so it is seen.
export const AFTER_FADE = 0.45;
export const stageClass = (stage: Stage) => (stage === 'rest' ? '' : `oh-${stage}`);

// Arms before the first paint, so the final drawing never flashes, and plays when the
// figure's top reaches the upper three quarters of the screen.
export function usePlayOnce<T extends Element>(): [RefObject<T>, Stage, () => void] {
  const ref = useRef<T>(null);
  const [stage, setStage] = useState<Stage>('rest');

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined' || reducedMotion()) return undefined;
    setStage('armed');
    const seen = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setStage('play');
      seen.disconnect();
    }, { rootMargin: '0px 0px -25% 0px' });
    seen.observe(el);
    return () => seen.disconnect();
  }, []);

  // Back to the start for a frame, then play again: CSS animations restart on the new class.
  const replay = useCallback(() => {
    if (reducedMotion()) return;
    setStage('armed');
    requestAnimationFrame(() => requestAnimationFrame(() => setStage('play')));
  }, []);

  return [ref, stage, replay];
}
